import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';
import { categories, getMoviesByCategory } from '@/mocks/movies';

const sortOptions = [
  { id: 'default', name: '默认排序' },
  { id: 'rating', name: '评分最高' },
  { id: 'newest', name: '最新上线' },
  { id: 'popular', name: '最多观看' },
];

const categoryMeta: Record<string, { title: string; description: string; keywords: string }> = {
  movie: {
    title: '电影大全',
    description: '成全影院电影大全，海量高清电影免费在线观看。涵盖动作片、科幻片、喜剧片、爱情片、恐怖片、战争片等各类型电影，每日更新最新大片。',
    keywords: '电影,免费电影,电影大全,在线观看,高清电影,最新电影,成全影院',
  },
  tv: {
    title: '电视剧全集',
    description: '成全影院电视剧全集，海量高清电视剧免费在线观看。国产剧、港台剧、日韩剧、欧美剧应有尽有，热门电视剧每日更新，追剧就来成全影院。',
    keywords: '电视剧,电视剧全集,免费电视剧,在线观看,国产剧,热门电视剧,成全影院',
  },
  variety: {
    title: '综艺大全',
    description: '成全影院综艺大全，热门综艺免费在线观看。音乐竞演、户外真人秀、脱口秀、生活纪实等各类综艺节目，每周更新，精彩不断。',
    keywords: '综艺,综艺大全,免费综艺,在线观看,真人秀,热门综艺,成全影院',
  },
  anime: {
    title: '动漫大全',
    description: '成全影院动漫大全，热门动漫免费在线观看。国产动漫、日本动漫、欧美动漫，热血、玄幻、搞笑、恋爱各类型动漫，每日更新。',
    keywords: '动漫,动漫大全,免费动漫,在线观看,国产动漫,日本动漫,成全影院',
  },
};

export default function Category() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const currentCategoryId = categoryId || 'movie';
  const [activeCategory, setActiveCategory] = useState(currentCategoryId);
  const [sortBy, setSortBy] = useState('default');

  const currentCategory = categories.find((c) => c.id === activeCategory) || categories[0];
  const categoryData = categoryMeta[activeCategory] || categoryMeta.movie;

  const movies = useMemo(() => {
    let list = getMoviesByCategory(activeCategory);

    switch (sortBy) {
      case 'rating':
        return list.sort((a, b) => b.rating - a.rating);
      case 'newest':
        return list.sort((a, b) => b.year - a.year || b.rating - a.rating);
      case 'popular':
        return list.sort((a, b) => b.views - a.views);
      default:
        return list;
    }
  }, [activeCategory, sortBy]);

  const handleCategoryChange = (id: string) => {
    setActiveCategory(id);
    setSortBy('default');
    window.history.replaceState(null, '', `/category/${id}`);
  };

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryData.title} - 成全影院`,
    description: categoryData.description,
    url: `https://chengquan.tv/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '成全影院',
      url: 'https://chengquan.tv/',
    },
  };

  return (
    <>
      <SEO
        title={categoryData.title}
        description={categoryData.description}
        keywords={categoryData.keywords}
        canonicalUrl={`https://chengquan.tv/category/${activeCategory}`}
        schema={categorySchema}
      />

      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryData.title },
              ]}
            />

            <div className="mt-6 mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-2">
                {categoryData.title}
              </h1>
              <p className="text-sm text-foreground-400 max-w-2xl">{categoryData.description}</p>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-1.5 mb-8 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
              <div className="flex items-center bg-background-900 rounded-full p-1 gap-0.5">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.id
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-400 hover:text-foreground-200'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-sm`}></i>
                    </div>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort Options */}
            <div className="flex items-center gap-2 mb-6">
              <span className="text-sm text-foreground-500 mr-1">排序：</span>
              {sortOptions.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setSortBy(opt.id)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    sortBy === opt.id
                      ? 'bg-secondary-500/20 text-secondary-300 border border-secondary-500/30'
                      : 'text-foreground-400 hover:text-foreground-200 border border-transparent'
                  }`}
                >
                  {opt.name}
                </button>
              ))}
            </div>

            {/* Movie Grid */}
            {movies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {movies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} size="medium" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-4xl text-foreground-500"></i>
                </div>
                <p className="text-foreground-400 text-sm">暂无相关内容</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}